import { Component, ViewChild, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ListDataService } from '../services/list-data.service';
import { UserDataService } from '../services/user-data.service';
import { UserSettingsService } from '../services/user-settings.service';
import { ScaleViewService } from '../services/scale-view.service';
import { LocationType } from '../models/Enums';
import { ListItem } from '../models/ListItem';
import { ScheduleParameters } from '../models/ScheduleParameters';
import { ScaleViewLoad } from '../models/ScaleViewLoad';
import { ScaleViewAddLoadResult } from '../models/ScaleViewAddLoadResult';
import { PqgridComponent } from '../pqgrid/pqgrid.component';
import pq from 'pqgrid';

@Component({
    selector: 'scale-view',
    standalone: false,
    templateUrl: 'scale-view.component.html',
    styleUrls: ['scale-view.component.css']
})
export class ScaleViewComponent implements OnInit, OnDestroy {
    private readonly _destroyRef = inject(DestroyRef);
    private readonly TOAST_DURATION_MS = 3000;

    constructor(
        private _listDataService: ListDataService,
        private _userDataService: UserDataService,
        private _userSettingsService: UserSettingsService,
        private _scaleViewService: ScaleViewService
    ) { }

    @ViewChild('scaleViewGrid') scaleViewGrid!: PqgridComponent;
    scheduleParameters: ScheduleParameters = new ScheduleParameters();
    invalidDateRange: boolean = false;
    isLoadingData: boolean = false;

    locList: ListItem[] = [];
    planPeriods: ListItem[] = [];
    loads: ScaleViewLoad[] = [];

    errorMessage: string = '';
    noTradeUnitAssigned: boolean = false;
    compuweighNotEnabled: boolean = false;
    lastRefreshed: Date | null = null;
    includeDeliveredAndPreadvised: boolean = false;
    selectedLoad: ScaleViewLoad | null = null;
    isAddingLoad: boolean = false;
    showToast: boolean = false;
    toastMessage: string = '';
    toastType: 'success' | 'error' | 'info' = 'info';

    isOnline: boolean = navigator.onLine;
    private loadIdsBeforeAdd: Set<number> = new Set();
    private onlineListener: () => void = () => {};
    private offlineListener: () => void = () => {};

    scaleViewColumns = [
        {
            title: "",
            width: 50,
            dataType: "string",
            align: 'center',
            dataIndx: "selected",
            editable: false,
            render: (ui: any) => {
                const isSelected = this.selectedLoad?.loadId === ui.rowData.loadId;
                return `<input type="checkbox" ${isSelected ? 'checked' : ''} style="cursor: pointer;" />`;
            }
        },
        {
            title: "Trucker",
            width: 400,
            dataType: "string",
            align: 'left',
            dataIndx: "trucker",
            editable: false,
            filter: { crules: [{ condition: "contain" }] }
        },
        {
            title: "Origin/Destination",
            minWidth: 500,
            dataType: "string",
            dataIndx: "originDestination",
            editable: false,
            filter: { crules: [{ condition: "contain" }] }
        },
        {
            title: "Comm",
            minWidth: 80,
            dataType: "string",
            align: 'center',
            dataIndx: "commodity",
            editable: false,
            filter: { crules: [{ condition: "contain" }] }
        },
        {
            title: "Load Date/Range",
            width: 180,
            dataType: "string",
            align: 'center',
            dataIndx: "loadDateRange",
            editable: false,
            filter: { crules: [{ condition: "contain" }] }
        },
        {
            title: "Scout Load ID",
            minWidth: 150,
            dataType: "integer",
            align: 'center',
            dataIndx: "loadId",
            editable: false,
            filter: { crules: [{ condition: "contain" }] },
            render: (ui: any) => {
                return `<span>${ui.rowData.loadId}</span> <i class="fa-solid fa-copy fa-lg" style="cursor: pointer; margin-left: 8px;" title="Copy load number" data-load-id="${ui.rowData.loadId}"></i>`;
            }
        },
        {
            title: "Compuweigh Status",
            width: 150,
            dataType: "string",
            align: 'center',
            dataIndx: "compuweighStatus",
            editable: false,
            render: (ui: any) => {
                const status = ui.rowData.compuweighStatus;
                if (!status) return '';
                let color = '';
                let tooltipText = '';
                if (status === 'Success') {
                    color = '#28a745';
                    tooltipText = 'Sent to Compuweigh';
                } else if (status === 'In-Progress') {
                    color = '#ffc107';
                    tooltipText = 'Sending to Compuweigh';
                } else if (status === 'Failed') {
                    color = '#dc3545';
                    tooltipText = 'Compuweigh send failed';
                } else {
                    return `<span>${status}</span>`;
                }
                return `<i class="fa-solid fa-circle" style="color: ${color}; font-size: 1em;" title="${tooltipText}"></i>`;
            }
        }
    ];

    scaleViewOptions: pq.gridT.options = {
        menuIcon: false,
        showTop: false,
        roundCorners: true,
        reactive: true,
        locale: "en",
        width: "flex",
        height: "flex",
        fillHandle: "",
        freezeCols: 1,
        resizable: true,
        autoRow: false,
        numberCell: {
            show: false
        },
        colModel: this.scaleViewColumns,
        animModel: {
            on: true
        },
        dataModel: {
            data: []
        },
        sortModel: {
            cancel: true,
            type: "local",
            single: false,
            space: false
        },
        filterModel: {
            header: true,
            menuIcon: false
        },
        rowClick: (evt: any, ui: any) => this.handleGridRowClick(evt, ui),
        rowInit: (ui: any) => {
            if (this.selectedLoad?.loadId === ui.rowData.loadId) {
                return { cls: 'pq-row-select' };
            }
            return {};
        }
    }

    private handleGridRowClick(evt: any, ui: any): void {
        if (this.isCopyIconClick(evt)) {
            const loadId = this.getLoadIdFromClickEvent(evt);
            if (loadId) {
                this.copyLoadNumber(loadId);
            }
            return;
        }
        this.onRowClick(ui.rowData);
    }

    private isCopyIconClick(evt: any): boolean {
        const target = evt.originalEvent?.target || evt.target;
        return target && (target.classList?.contains('fa-copy') || target.closest?.('.fa-copy'));
    }

    private getLoadIdFromClickEvent(evt: any): string | null {
        const target = evt.originalEvent?.target || evt.target;
        return target.getAttribute('data-load-id') ||
               target.closest('.fa-copy')?.getAttribute('data-load-id');
    }

    private setupOnlineOfflineListeners(): void {
        this.onlineListener = () => {
            this.isOnline = true;
        };

        this.offlineListener = () => {
            this.isOnline = false;
        };

        window.addEventListener('online', this.onlineListener);
        window.addEventListener('offline', this.offlineListener);
    }

    copyLoadNumber(loadId: string): void {
        navigator.clipboard.writeText(loadId).then(() => {
            this.showToastMessage(`Load number ${loadId} copied!`);
        }).catch(() => {
            this.showToastMessage('Failed to copy load number');
        });
    }

    private showToastMessage(message: string, type: 'success' | 'error' | 'info' = 'info'): void {
        this.toastMessage = message;
        this.toastType = type;
        this.showToast = true;
        setTimeout(() => this.showToast = false, this.TOAST_DURATION_MS);
    }

    onRowClick(rowData: ScaleViewLoad): void {
        if (this.selectedLoad?.loadId === rowData.loadId) {
            this.selectedLoad = null;
        } else {
            this.selectedLoad = rowData;
        }

        if (this.scaleViewGrid && this.scaleViewGrid.grid) {
            this.scaleViewGrid.grid.refresh();
        }
    }

    private highlightNewlyAddedRow(loadId: number): void {
        if (!this.scaleViewGrid?.grid) {
            return;
        }

        const grid = this.scaleViewGrid.grid;
        const visibleData = grid.getData();
        const rowIndex = visibleData.findIndex((row: any) => row.loadId === loadId);

        if (rowIndex !== -1) {
            setTimeout(() => {
                grid.addClass({ rowIndx: rowIndex, cls: 'pq-row-highlight' });

                setTimeout(() => {
                    grid.removeClass({ rowIndx: rowIndex, cls: 'pq-row-highlight' });
                    grid.refresh();
                }, 3000);
            }, 200);
        }
    }

    duplicateSelectedLoad(): void {
        if (!this.isOnline || !this.selectedLoad || this.compuweighNotEnabled) {
            return;
        }

        this.isAddingLoad = true;
        this.errorMessage = '';
        this.loadIdsBeforeAdd = new Set(this.loads.map(load => load.loadId));

        this._scaleViewService.addLoad({ loadId: this.selectedLoad.loadId })
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: (result: ScaleViewAddLoadResult) => {
                    this.selectedLoad = null;
                    this.loadScaleViewData();
                    this.isAddingLoad = false;

                    if (result.compuweighSent) {
                        if (result.compuweighSuccess) {
                            this.showToastMessage('Load added and sent to CompuWeigh', 'success');
                        } else {
                            this.showToastMessage('Load added but CompuWeigh send failed', 'error');
                        }
                    } else {
                        this.showToastMessage('Load added', 'info');
                    }
                },
                error: (err) => {
                    this.errorMessage = err.message;
                    this.isAddingLoad = false;
                    this.loadIdsBeforeAdd.clear();
                }
            });
    }

    loadScaleViewData(): void {
        if (!this.isOnline) {
            this.errorMessage = 'Cannot load data while offline';
            return;
        }
        if (!this.validateDates() || this.scheduleParameters.locCode.length === 0) {
            return;
        }

        this.isLoadingData = true;
        this.errorMessage = '';

        this._scaleViewService.getScaleViewLoads(
            this.scheduleParameters.locCode,
            this.scheduleParameters.periodStartDate,
            this.scheduleParameters.periodEndDate,
            this.scheduleParameters.locationType,
            this.includeDeliveredAndPreadvised
        ).pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
            next: (data) => {
                this.loads = this.transformLoads(data);
                this.updateGrid(this.loads);
                this.lastRefreshed = new Date();
                this.isLoadingData = false;
                this.highlightNewlyAddedLoadIfPresent();
            },
            error: (err) => {
                this.errorMessage = err.message;
                this.isLoadingData = false;
                this.loadIdsBeforeAdd.clear();
            }
        });
    }

    private transformLoads(data: ScaleViewLoad[]): ScaleViewLoad[] {
        return data.map(load => ({
            ...load,
            originDestination: `${load.originID} ${load.destinationID}`
        }));
    }

    private updateGrid(loads: ScaleViewLoad[]): void {
        if (this.scaleViewGrid?.grid) {
            this.scaleViewGrid.grid.option('dataModel.data', loads);
            this.scaleViewGrid.grid.refreshDataAndView();
        }
    }

    private highlightNewlyAddedLoadIfPresent(): void {
        if (this.loadIdsBeforeAdd.size === 0) {
            return;
        }

        const newLoad = this.loads.find(load => !this.loadIdsBeforeAdd.has(load.loadId));
        if (newLoad) {
            setTimeout(() => {
                this.highlightNewlyAddedRow(newLoad.loadId);
                this.loadIdsBeforeAdd.clear();
            }, 100);
        }
    }

    loadPlanPeriods(): void {
        if (this.scheduleParameters.locCode.length === 0) {
            return;
        }

        this._listDataService.getPlanPeriods(this.scheduleParameters.locCode)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: data => {
                    this.planPeriods = data;
                    if (this.planPeriods.length > 0) {
                        const planPeriodExists = this.planPeriods.some(item => item.value === this.scheduleParameters.planPeriod);
                        if (!planPeriodExists) {
                            this.scheduleParameters.planPeriod = this.planPeriods[0].value;
                        }
                        this.loadPeriodDatesAndRefresh();
                    }
                },
                error: err => this.errorMessage = err
            });
    }

    loadPeriodDatesAndRefresh(): void {
        this._listDataService.getPeriodDates(this.scheduleParameters.planPeriod)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: data => {
                    this.scheduleParameters.periodStartDate = data.startDate;
                    this.scheduleParameters.periodEndDate = data.endDate;
                    this.scheduleParameters.shipThroughDate = data.shipThroughDate;
                    this.loadScaleViewData();
                },
                error: err => this.errorMessage = err
            });
    }

    get locDisplayCode(): string {
        const item = this.locList.find(l => l.value === this.scheduleParameters.locCode);
        return item ? item.text.split(' - ')[0] : this.scheduleParameters.locCode;
    }

    onLocCodeChange(): void {
        this._userSettingsService.setCurrentLocCode(this.scheduleParameters.locCode);
        this.noTradeUnitAssigned = this.scheduleParameters.locCode.length === 0;
        this.loadCompuweighEnabled();
        this.loadLocationDefaults();
    }

    loadCompuweighEnabled(): void {
        if (this.scheduleParameters.locCode.length === 0) {
            this.compuweighNotEnabled = false;
            return;
        }
        this._scaleViewService.resolveCompuweighEnabled(this.scheduleParameters.locCode)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(enabled => this.compuweighNotEnabled = !enabled);
    }

    onPlanPeriodChange(): void {
        if (this.scheduleParameters.planPeriod !== 'Custom') {
            this.loadPeriodDatesAndRefresh();
        }
    }

    validateDates(): boolean {
        this.invalidDateRange = false;
        const startDate = new Date(this.scheduleParameters.periodStartDate);
        const endDate = new Date(this.scheduleParameters.periodEndDate);
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime()) ||
            startDate.getTime() > endDate.getTime()) {
            this.invalidDateRange = true;
            return false;
        }
        return true;
    }

    loadLocationDefaults(): void {
        if (this.scheduleParameters.locCode.length === 0) {
            return;
        }

        this.scheduleParameters.locationType = LocationType.Destination;
        this.scheduleParameters.planPeriod = 'WeekCurrent';

        const keys = ['DefaultPlanningPerspective', 'DefaultPlanningWindow'];
        this._listDataService.getLocDefaults(this.scheduleParameters.locCode, keys)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: data => {
                    const planByDefault = data.find(x => x.key === 'DefaultPlanningPerspective');
                    if (planByDefault !== undefined && planByDefault.value.length > 0) {
                        this.scheduleParameters.locationType = planByDefault.value === 'Origin' ? LocationType.Origin : LocationType.Destination;
                    }
                    const planPeriodDefault = data.find(x => x.key === 'DefaultPlanningWindow');
                    if (planPeriodDefault !== undefined && planPeriodDefault.value.length > 0) {
                        this.scheduleParameters.planPeriod = planPeriodDefault.value;
                    }

                    this.loadPlanPeriods();
                },
                error: err => { this.errorMessage = err; }
            });
    }

    ngOnInit(): void {
        const sessionStoreLocCode = sessionStorage.getItem("locCode");
        this.scheduleParameters.locationType = LocationType.Destination;
        this.scheduleParameters.planPeriod = 'WeekCurrent';
        this.setupOnlineOfflineListeners();

        this._listDataService.getListItems("loc")
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: data => {
                    this.locList = data;
                },
                error: err => this.errorMessage = err
            });

        this._userDataService.getUserSettings()
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: data => {
                    this.scheduleParameters.locCode = sessionStoreLocCode || data.defaultLocCode;
                    this._userSettingsService.setCurrentLocCode(this.scheduleParameters.locCode);
                    this.noTradeUnitAssigned = this.scheduleParameters.locCode.length === 0;
                    this.loadCompuweighEnabled();
                    this.loadLocationDefaults();
                },
                error: err => this.errorMessage = err
            });
    }

    ngOnDestroy(): void {
        if (this.onlineListener) {
            window.removeEventListener('online', this.onlineListener);
        }
        if (this.offlineListener) {
            window.removeEventListener('offline', this.offlineListener);
        }
    }
}